import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { ChevronRightIcon } from '@heroicons/react/24/solid';
import { Button } from '@leadsales/design-system/react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { VITE_ONBOARDING_LIVE_SESSION_URL } from 'src/constants/envs';
import {
  WHATSAPP_BUSINESS_NO_WABA_IN_TOKEN,
  WHATSAPP_BUSINESS_WABA_IN_USE_BY_ANOTHER_WORKSPACE,
  WHATSAPP_SDK_TIMEOUT_ERROR_CODE,
} from 'src/constants/whatsappBusinessErrorCodes.constants';
import { useBusinessHours } from 'src/hooks/whatsapp/useBusinessHours.hook';
import { useAdvisorScheduleUrl } from 'src/hooks/whatsapp/useAdvisorScheduleUrl.hook';
import type { tConnectionFailedErrorProps } from './ConnectionFailedError.types';
import '../WhatsAppConnect.shared.styles.scss';

const DEFAULT_NAMESPACE = 'whatsappConnect/whatsappConnect';

type tConnectionFailureCopy = {
  titleKey: string;
  titleFallback: string;
  subtitleKey: string;
  subtitleFallback: string;
  primaryCtaKey: string;
  primaryCtaFallback: string;
};

const GENERIC_FAILURE_COPY: tConnectionFailureCopy = {
  titleKey: 'failedError.title',
  titleFallback: 'Meta no permitió la conexión',
  subtitleKey: 'failedError.subtitle',
  subtitleFallback:
    'Intenta de nuevo. Si el problema continúa, pide ayuda a uno de nuestros asesores.',
  primaryCtaKey: 'footer.retry',
  primaryCtaFallback: 'Intentar de nuevo',
};

// A known backend code selects its own copy; anything else (absent, unknown
// future codes, restored sessions) falls back to GENERIC_FAILURE_COPY. New
// codes are registered here instead of branching in the component body.
const FAILURE_COPY_BY_ERROR_CODE: Record<string, tConnectionFailureCopy | undefined> = {
  [WHATSAPP_BUSINESS_NO_WABA_IN_TOKEN]: {
    titleKey: 'failedError.noWaba.title',
    titleFallback: 'Falta conceder acceso a tu WhatsApp Business',
    subtitleKey: 'failedError.noWaba.subtitle',
    subtitleFallback:
      'Meta nos indicó que no se otorgó acceso a ninguna cuenta de WhatsApp Business. Cuando Meta te muestre los permisos, selecciona la cuenta de WhatsApp Business que ya tienes registrada. Si no la ves, verifica tu negocio en Meta Business Manager e inténtalo de nuevo.',
    primaryCtaKey: GENERIC_FAILURE_COPY.primaryCtaKey,
    primaryCtaFallback: GENERIC_FAILURE_COPY.primaryCtaFallback,
  },
  // Retrying reopens Meta's selector, which is where a different number gets
  // picked, so the primary button keeps onRetry under its own label.
  [WHATSAPP_BUSINESS_WABA_IN_USE_BY_ANOTHER_WORKSPACE]: {
    titleKey: 'failedError.wabaInUse.title',
    titleFallback: 'Este número ya está conectado en otro espacio de Leadsales',
    subtitleKey: 'failedError.wabaInUse.subtitle',
    subtitleFallback:
      'Un número de WhatsApp solo puede estar conectado en un espacio de Leadsales a la vez. Para usarlo aquí, primero hay que liberarlo en el espacio donde está hoy; también puedes elegir un número distinto. Si no sabes en cuál quedó, un asesor lo identifica contigo.',
    primaryCtaKey: 'failedError.wabaInUse.cta',
    primaryCtaFallback: 'Elegir otro número',
  },
  // Meta's window never opened, so the copy names the page load, not Meta.
  [WHATSAPP_SDK_TIMEOUT_ERROR_CODE]: {
    titleKey: 'failedError.sdkTimeout.title',
    titleFallback: 'No se pudo abrir la ventana de Meta',
    subtitleKey: 'failedError.sdkTimeout.subtitle',
    subtitleFallback:
      'La ventana de Meta no terminó de cargar. Puede pasar con una conexión lenta o con un bloqueador de contenido en tu navegador. Revisa tu conexión e intenta de nuevo.',
    primaryCtaKey: GENERIC_FAILURE_COPY.primaryCtaKey,
    primaryCtaFallback: GENERIC_FAILURE_COPY.primaryCtaFallback,
  },
};

export default function ConnectionFailedError(props: tConnectionFailedErrorProps) {
  const { t } = useTranslation(props.translationNamespace ?? DEFAULT_NAMESPACE);
  const { isLiveSessionAvailable } = useBusinessHours();
  const scheduleUrl = useAdvisorScheduleUrl();

  const copy = FAILURE_COPY_BY_ERROR_CODE[props.errorCode ?? ''] ?? GENERIC_FAILURE_COPY;
  const title = t(copy.titleKey, copy.titleFallback);
  const subtitle = t(copy.subtitleKey, copy.subtitleFallback);
  const primaryCtaLabel = t(copy.primaryCtaKey, copy.primaryCtaFallback);

  const advisorHref = isLiveSessionAvailable ? VITE_ONBOARDING_LIVE_SESSION_URL : scheduleUrl;
  const advisorKind = isLiveSessionAvailable ? 'live' : 'schedule';
  const advisorLabel = isLiveSessionAvailable
    ? t('footer.live', 'Unirme a sesión en vivo')
    : t('footer.schedule', 'Agendar con un asesor');
  const feedbackText = isLiveSessionAvailable
    ? t(
        'failedError.feedbackInHours',
        'Hay un asesor en sesión en vivo ahora. Da clic en "Unirme a sesión en vivo" abajo para que te ayude a conectar.',
      )
    : t(
        'failedError.feedbackOutOfHours',
        'Las sesiones en vivo con un asesor están disponibles Lunes a Viernes de 9:00 a 18:00 (hora CDMX). Da clic en "Agendar con un asesor" abajo para reservar una.',
      );

  function handleAdvisorClick() {
    props.onAdvisorCtaClick?.(advisorKind);
  }

  return (
    <div className="flex flex-col flex-1">
      <div className="flex flex-col items-center justify-center gap-6 py-12 text-center flex-1">
        <ExclamationTriangleIcon className="w-16 h-16 text-warning" aria-hidden="true" />

        <div className="flex flex-col gap-2 max-w-lg">
          <h2 className="text-2xl font-semibold text-base-content">{title}</h2>
          <p className="text-base text-base-content/60">{subtitle}</p>
        </div>

        {props.advisorRow ?? (
          <div
            className={classnames(
              'flex items-start gap-2 px-4 py-3 rounded-lg max-w-xl text-sm leading-relaxed border',
              {
                'bg-success/10 border-success/30 text-base-content':
                  isLiveSessionAvailable,
                'bg-info/5 border-info/30 text-base-content/80':
                  !isLiveSessionAvailable,
              },
            )}
          >
            <span aria-hidden="true" className="text-base">
              {isLiveSessionAvailable ? '🟢' : '📅'}
            </span>
            <span>{feedbackText}</span>
          </div>
        )}
      </div>

      <div className="WhatsAppConnect__actions">
        <Button color="tertiary" size="md" type="button" onClick={props.onBack}>
          {t('footer.back', 'Regresar')}
        </Button>
        <div className="WhatsAppConnect__actionsRight">
          {!props.advisorRow && (
            <a
              href={advisorHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleAdvisorClick}
              className="btn btn-outline btn-md"
            >
              {advisorLabel}
            </a>
          )}
          <Button color="primary" size="md" type="button" onClick={props.onRetry}>
            {primaryCtaLabel}
            <ChevronRightIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
